import { forwardRef, PropsWithChildren, ReactNode } from "react"
import { Theme, styled, SxProps, Box, Typography } from "@mui/material"
import { classes } from "@tm/utils"
import { simpleWidgetCardClasses, simpleWidgetCardName, simpleWidgetCardSlots } from "@tm/themes"
import { Icon, IconProps } from "../../muiStyled/DataDisplay/Icons"
import { SimpleWidgetCardCover } from "./components/Cover"
import { SimpleWidgetCardCard } from "./components/Card"
import { SimpleWidgetCardContent } from "./components/Content"
import { SimpleWidgetCardHeader } from "./components/Header"
import { SimpleWidgetCardActions } from "./components/Actions"

export type SimpleWidgetCardProps = {
    widgetVariant: "startpage" | "dashboard"
    active?: boolean
    title?: React.ReactNode
    icon?: string
    iconProps?: IconProps
    sx?: SxProps<Theme>
    headerAction?: ReactNode
    footerAction?: ReactNode
    className?: string
    handleCoverClick?: () => void
    // eslint-disable-next-line react/no-unused-prop-types
    isVariableHeight?: boolean
}

const SimpleWidgetCardComponent = forwardRef<HTMLDivElement, PropsWithChildren<SimpleWidgetCardProps>>(function SimpleWidgetCard(props, ref) {
    const { active = true, title, children, icon, sx, footerAction, headerAction, handleCoverClick, className, iconProps } = props

    function renderTitle() {
        return (
            <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1" color="inherit" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {icon && <Icon name={icon} {...iconProps} sx={{ fontSize: "calc(1em + 2px)" }} />}
                    {title}
                </Typography>
            </Box>
        )
    }

    return (
        <Box className={classes(simpleWidgetCardClasses.root, className)} ref={ref}>
            {!active && <SimpleWidgetCardCover title={title} icon={icon} onCoverClick={handleCoverClick} sx={{ ...sx }} />}
            {active && (
                <SimpleWidgetCardCard className={simpleWidgetCardClasses.card}>
                    {title && <SimpleWidgetCardHeader className={simpleWidgetCardClasses.header} title={renderTitle()} action={headerAction} />}
                    <SimpleWidgetCardContent className={simpleWidgetCardClasses.content} hasTitle={!!title}>
                        {children}
                    </SimpleWidgetCardContent>
                    {footerAction && <SimpleWidgetCardActions className={simpleWidgetCardClasses.actions}>{footerAction}</SimpleWidgetCardActions>}
                </SimpleWidgetCardCard>
            )}
        </Box>
    )
})

export const SimpleWidgetCard = styled(SimpleWidgetCardComponent, {
    name: simpleWidgetCardName,
    slot: simpleWidgetCardSlots.root,
    overridesResolver: (_, styles) => styles.root,
})(({ isVariableHeight }) => ({
    width: "100%",
    height: isVariableHeight ? "auto" : "100%",
    padding: ".5em .75em",
}))
